import { ComponentBase } from "../../core/ComponentBase.js";
import { ChatMessage } from "./ChatMessage.js";
import { normalizeTurnArtifactMessages } from "./TurnArtifactMessageNormalizer.js";

export class ChatMessageList extends ComponentBase {
    constructor(config) {
        super(config);
        this.workspace = config.workspace;
        this.scrollEl = config.scrollEl;
        this.messagesInner = config.messagesInner;
        this.liveArea = config.liveArea;
        this.activeSkillCards = config.activeSkillCards || new Map();
        this.runningJobIds = config.runningJobIds || new Set();

        this._messages = [];
        this._rawMessages = [];
        this._hasMore = true;
        this._isLoadingOlder = false;
        this._isSettingMessages = false;
        this._isInitialLoad = true;
        this._hasFuture = false;
        this._suppressScrollPin = false;

        this._scrollHandler = this._onScroll.bind(this);
        this.scrollEl.addEventListener('scroll', this._scrollHandler, { passive: true });

        if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }

        // High-performance ResizeObserver to pin scroll to bottom during dynamic child rendering
        this._resizeObserver = new ResizeObserver(() => {
            if (this._isSettingMessages && this.scrollEl && !this._suppressScrollPin) {
                this.scrollEl.scrollTop = this.scrollEl.scrollHeight;
            }
        });
        if (this.messagesInner) {
            this._resizeObserver.observe(this.messagesInner);
        }
    }

    _onScroll() {
        if (this._isSettingMessages || this._suppressScrollPin) return;

        // Scroll near top: load older history
        if (this._hasMore && !this._isLoadingOlder && this.scrollEl.scrollTop < 100) {
            this._loadOlderMessages();
            return;
        }

        // Scroll near bottom: reload latest view if we have trimmed future messages
        if (this._hasFuture && !this._isLoadingOlder) {
            const distanceFromBottom = this.scrollEl.scrollHeight - this.scrollEl.scrollTop - this.scrollEl.clientHeight;
            if (distanceFromBottom < 100) {
                this._reloadLatestMessages();
            }
        }
    }

    async _reloadLatestMessages() {
        this._hasFuture = false;
        if (this.workspace.chatAreaComponent) {
            await this.workspace.chatAreaComponent.loadHistory(this.workspace.currentThreadId);
        }
    }

    async _loadOlderMessages() {
        if (!this._hasMore || this._isLoadingOlder) return;
        this._isLoadingOlder = true;

        // Prepend elegant, non-intrusive loading spinner at the top
        const loader = document.createElement('div');
        loader.className = 'ws-history-loader';
        loader.innerHTML = `
            <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
            <span class="text-muted small">Loading history...</span>
        `;
        if (this.messagesInner.firstChild) {
            this.messagesInner.insertBefore(loader, this.messagesInner.firstChild);
        } else {
            this.messagesInner.appendChild(loader);
        }

        const oldestMsg = this._rawMessages[0] || this._messages[0];
        const before = oldestMsg ? oldestMsg.createdAt : null;

        try {
            const res = await this.appClient.rpcClient.call('ChatRpc.getThreadMessages', {
                threadId: this.workspace.currentThreadId,
                before: before,
                limit: 20
            }, { projectId: this.workspace.currentProjectId });

            const newMsgs = res.messages || [];
            this._hasMore = res.hasMore;

            // Instantly remove loading spinner before updating anchor coordinates
            if (loader.parentNode) {
                loader.parentNode.removeChild(loader);
            }

            if (newMsgs.length > 0) {
                this.prependMessages(newMsgs);
            }
        } catch (e) {
            console.error("Failed to load older messages:", e);
            if (loader.parentNode) {
                loader.parentNode.removeChild(loader);
            }
        } finally {
            this._isLoadingOlder = false;
        }
    }

    setMessages(messages, hasMore = false) {
        this._isSettingMessages = true;
        const isJumping = !!this.workspace._pendingMessageJump;
        if (isJumping) {
            this._suppressScrollPin = true;
        }
        try {
            const newNormalized = normalizeTurnArtifactMessages(messages);
            const canReconcile = !this._isInitialLoad && !isJumping && this._messages && this._messages.length > 0 && messages.length > 0;

            if (canReconcile) {
                const context = {
                    appClient: this.appClient,
                    workspace: this.workspace,
                    createManagedComponent: this.config.createManagedComponent,
                    isHistoryLoad: true,
                    onSkillCardCreated: (jobId, cardPromise) => {
                        this.activeSkillCards.set(jobId, cardPromise);
                    }
                };

                // Map existing elements by message ID
                const existingElements = new Map();
                for (const child of Array.from(this.messagesInner.children)) {
                    const msgId = child.getAttribute('data-message-id');
                    if (msgId) {
                        existingElements.set(msgId, child);
                    }
                }

                // We will build a new child list matching the order of newNormalized
                // and reuse existing DOM nodes if their metadata/content hasn't changed.
                const newElements = [];
                const seenIds = new Set();

                newNormalized.forEach((newMsg) => {
                    const msgId = newMsg._id?.toString();
                    if (msgId) {
                        seenIds.add(msgId);
                    }

                    const existingNode = msgId ? existingElements.get(msgId) : null;
                    const oldMsg = msgId ? this._messages.find(m => m._id?.toString() === msgId) : null;

                    let hasChanged = true;
                    if (existingNode && oldMsg) {
                        const oldStr = JSON.stringify({ role: oldMsg.role, content: oldMsg.content, metadata: oldMsg.metadata });
                        const newStr = JSON.stringify({ role: newMsg.role, content: newMsg.content, metadata: newMsg.metadata });
                        hasChanged = oldStr !== newStr;
                    }

                    if (existingNode && !hasChanged) {
                        newElements.push(existingNode);
                    } else {
                        const newNode = ChatMessage.render(newMsg, context);
                        if (newNode) {
                            if (existingNode) {
                                this.messagesInner.replaceChild(newNode, existingNode);
                            }
                            newElements.push(newNode);
                        }
                    }
                });

                // Remove elements that are no longer present in the new message list
                existingElements.forEach((node, id) => {
                    if (!seenIds.has(id)) {
                        if (node.parentNode) {
                            node.parentNode.removeChild(node);
                        }
                    }
                });

                // Align the children of messagesInner to match newElements order perfectly
                newElements.forEach((node, idx) => {
                    const currentChild = this.messagesInner.children[idx];
                    if (currentChild !== node) {
                        if (currentChild) {
                            this.messagesInner.insertBefore(node, currentChild);
                        } else {
                            this.messagesInner.appendChild(node);
                        }
                    }
                });

                // Remove any trailing child nodes that exceed newElements count (e.g. unmapped local messages)
                while (this.messagesInner.children.length > newElements.length) {
                    this.messagesInner.removeChild(this.messagesInner.lastChild);
                }

                this._rawMessages = messages;
                this._messages = newNormalized;
                this._hasMore = hasMore;
            } else {
                this.messagesInner.innerHTML = '';
                this._rawMessages = messages;
                this._messages = newNormalized;
                this._hasMore = hasMore;
                this._renderMessages(true);
            }
        } finally {
            // Cascade timeouts to ensure scroll position is perfectly painted and settled at the bottom
            // before allowing any scroll-triggered historical fetches.
            const settlementDelay = isJumping ? 100 : (this._isInitialLoad ? 1200 : 250); // Shorter settlement on jump to accelerate visual jump
            this._isInitialLoad = false;

            setTimeout(() => {
                if (this.scrollEl && !this._suppressScrollPin) {
                    this.scrollEl.scrollTop = this.scrollEl.scrollHeight;
                }
                setTimeout(() => {
                    if (this.scrollEl && !this._suppressScrollPin) {
                        this.scrollEl.scrollTop = this.scrollEl.scrollHeight;
                    }
                    this._isSettingMessages = false;
                }, settlementDelay);
            }, 50);
        }
    }

    prependMessages(older) {
        const previousScrollHeight = this.scrollEl?.scrollHeight || 0;

        this._rawMessages = [...older, ...this._rawMessages];
        this._messages = normalizeTurnArtifactMessages(this._rawMessages);

        this.messagesInner.innerHTML = '';
        this._renderMessages(true);

        // Memory ceiling: Trim newest off-screen messages from bottom if we exceed the safety ceiling (100 messages)
        const MAX_DOM_CEILING = 100;
        if (this._messages.length > MAX_DOM_CEILING) {
            const excess = this._messages.length - MAX_DOM_CEILING;
            
            // Safe traversal to skip deleting the active AI reply streaming bubble if it exists in the DOM
            let removedCount = 0;
            let currentLast = this.messagesInner.lastChild;
            while (currentLast && removedCount < excess) {
                const prev = currentLast.previousSibling;
                if (currentLast !== this._streamWrapper) {
                    this.messagesInner.removeChild(currentLast);
                    removedCount++;
                }
                currentLast = prev;
            }

            this._messages = this._messages.slice(0, MAX_DOM_CEILING);
            this._hasFuture = true; // Mark that future messages are trimmed and can be reloaded
        }

        // Adjust scroll position to prevent visual jump
        requestAnimationFrame(() => {
            if (this.scrollEl) {
                const delta = this.scrollEl.scrollHeight - previousScrollHeight;
                this.scrollEl.scrollTop += delta;
            }
        });
    }

    appendMessage(msg) {
        this._rawMessages.push(msg);
        this._messages = normalizeTurnArtifactMessages(this._rawMessages);
        this.messagesInner.innerHTML = '';
        this._renderMessages(false);
    }

    _renderMessages(isHistoryLoad) {
        const context = {
            appClient: this.appClient,
            workspace: this.workspace,
            createManagedComponent: this.config.createManagedComponent,
            isHistoryLoad,
            onSkillCardCreated: (jobId, cardPromise) => {
                this.activeSkillCards.set(jobId, cardPromise);
            }
        };

        const frag = document.createDocumentFragment();
        this._messages.forEach(msg => {
            const node = ChatMessage.render(msg, context);
            frag.appendChild(node);
        });
        this.messagesInner.appendChild(frag);
    }

    // Streaming support API
    clearStream() {
        if (this._streamUpdateTimeout) {
            clearTimeout(this._streamUpdateTimeout);
            this._streamUpdateTimeout = null;
        }
        this._pendingStreamHtml = null;
        if (this._streamWrapper) {
            if (this._streamWrapper.parentNode) {
                this._streamWrapper.parentNode.removeChild(this._streamWrapper);
            }
            this._streamWrapper = null;
            this._streamBubble = null;
        }
    }

    updateStreamContent(html) {
        this._pendingStreamHtml = html;
        if (this._streamUpdateTimeout) return;

        this._streamUpdateTimeout = setTimeout(() => {
            this._streamUpdateTimeout = null;
            this._flushStreamUpdate();
        }, 80); // Throttled at most once every 80ms (12.5 FPS) to eliminate all visual flashing and layout thrashing
    }

    _flushStreamUpdate() {
        if (!this._pendingStreamHtml) return;

        if (!this._streamBubble) {
            this._streamWrapper = document.createElement('div');
            this._streamWrapper.className = 'ws-msg-wrapper assistant';
            
            this._streamBubble = document.createElement('div');
            this._streamBubble.className = 'ws-msg-bubble';
            this._streamWrapper.appendChild(this._streamBubble);
            
            const targetContainer = this.liveArea || this.messagesInner;
            targetContainer.appendChild(this._streamWrapper);
        }
        
        if (this._streamBubble.innerHTML !== this._pendingStreamHtml) {
            this._streamBubble.innerHTML = this._pendingStreamHtml;
            
            // Instantly keep scroll aligned with new content to prevent visual garbling
            if (this.scrollEl) {
                this.scrollEl.scrollTop = this.scrollEl.scrollHeight;
            }
        }
    }

    destroy() {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
        this.scrollEl?.removeEventListener('scroll', this._scrollHandler);
        if (this.messagesInner) this.messagesInner.innerHTML = '';
        this._messages = [];
        this._rawMessages = [];
        super.destroy();
    }
}
